// Shared color palette for chart overlays and UI elements
// Used by TradingView chart overlays and trades table checkboxes

export const OVERLAY_COLORS = [
  '#FF9800', // Orange
  '#4CAF50', // Green
  '#F44336', // Red
  '#00BCD4', // Cyan
  '#E91E63', // Pink
  '#9C27B0', // Purple
  '#3F51B5', // Indigo
  '#795548', // Brown
  '#607D8B', // Blue grey
  '#827717', // Olive
];

// Default color for TradingView Line Plots
export const MATERIAL_BLUE = '#2196F3';

export const getOverlayColor = (index: number): string => {
  return OVERLAY_COLORS[index % OVERLAY_COLORS.length];
};

// ---------------------------------------------------------------------------
// Chart line palette
//
// Shared by the web charts (seasonality.tsx, days-to-expiry-chart.tsx,
// forward-curve.tsx) and by the MCP widget bundle, whose modules import this
// file directly (apps/mcp-server/ui/src/*.ts). Both surfaces previously held
// hand-synced copies of everything below — the comments saying "kept in sync
// by hand" are what this module exists to delete. The MCP build already
// reaches across the repo for the charting_library and the watermark (see
// apps/mcp-server/ui/build.mjs), so it reaches across for the palette too
// rather than being allowed to drift again.
//
// Keep this file dependency-free: esbuild inlines it into a single
// self-contained HTML document that has no node_modules to resolve.
// ---------------------------------------------------------------------------

/**
 * HSL → hex. Standard CSS-Color-4 a/f conversion trick: f(0), f(8), f(4) give
 * R, G, B respectively.
 */
export function hslToHex (h: number, s: number, l: number): string {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): string => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    // Native padStart, not lodash: see the header — this module is bundled
    // into the MCP widget, which resolves no node_modules.
    // eslint-disable-next-line lodash/prefer-lodash-method
    return Math.round(255 * c).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// The house vintage convention, shared with the PowerBI dashboards and
// FundamentalsSeasonalityChart: the current series black, one step back red,
// two steps back green.
export const CURRENT_SERIES_COLOR = '#000000';
export const PREVIOUS_VINTAGE_COLOR = '#D32F2F';
export const SECOND_VINTAGE_COLOR = '#2E7D32';

// The live intraday forward curve — an accent, not a vintage.
export const LIVE_CURVE_COLOR = '#f97316';

// Same 8 hues the old tab20-derived palette used for its non-pastel entries
// (blue/orange/purple/teal/pink/brown/neutral/cyan) — yellow, lime, and every
// pastel are dropped for lacking contrast on the white chart background. Each
// "lap" through the 8 hues steps lightness up (older = lighter), capped at
// 0.72 so even a many-years-back vintage stays visibly a color, not cream.
export const TAIL_HUES = [207, 33, 261, 174, 316, 19, 0, 187];

export function vintageTailColor (i: number): string {
  const hue = TAIL_HUES[i % TAIL_HUES.length];
  const lap = Math.floor(i / TAIL_HUES.length);
  const sat = hue === 0 ? 0.08 : 0.35; // keep the "neutral" slot actually neutral
  const lightness = Math.min(0.72, 0.42 + lap * 0.14);
  return hslToHex(hue, sat, lightness);
}

/**
 * Color for a series `stepsBack` vintages behind the current one. Unbounded:
 * the tail keeps stepping lighter with age rather than wrapping, so a vintage
 * well beyond the named stops (days-to-expiry can add arbitrary ones via
 * AddSpreadMenu) never cycles back around to black.
 */
export function vintageColor (stepsBack: number): string {
  if (stepsBack <= 0) return CURRENT_SERIES_COLOR;
  if (stepsBack === 1) return PREVIOUS_VINTAGE_COLOR;
  if (stepsBack === 2) return SECOND_VINTAGE_COLOR;
  return vintageTailColor(stepsBack - 3);
}

// Seasonality indexes this by recency with a modulo, so it needs a fixed
// length: 3 named stops + 4 laps of the tail is far beyond any realistic
// lookback. Index 0 is a documented placeholder — the latest year always
// renders as CURRENT_SERIES_COLOR before this array is indexed.
export const YEAR_COLORS = Array.from({ length: 35 }, (_, i) => vintageColor(i));

// Forward vintages (days-to-expiry, unlike seasonality, can have future
// contracts) get a dedicated green ramp that fades — lighter and less
// saturated — the further out it goes, solid color throughout (no alpha) and
// clamped well short of white so even a distant forward vintage stays legible.
const FORWARD_STOPS = 12;
export const FORWARD_VINTAGE_COLORS = Array.from({ length: FORWARD_STOPS }, (_, i) => {
  const t = i / (FORWARD_STOPS - 1);
  return hslToHex(140, 0.55 - t * 0.15, 0.30 + t * 0.32); // green, L 30%→62%
});

/**
 * Historic forward-curve snapshots sit on a single solid blue ramp that gets
 * lighter the further back the snapshot is — no opacity, so a curve is never
 * faded toward invisible against the white chart background. `progress` runs 0
 * at the oldest snapshot to 1 at the most recent, excluding the live/current
 * curve, which stays black at 2px. Lightness is clamped to 66% so even the
 * oldest snapshot stays a visible blue, not a near-white smudge.
 */
export function forwardCurveHistoricColor (progress: number): string {
  return hslToHex(212, 0.65, 0.66 - progress * 0.42); // dark navy (recent) → clamped light blue (old)
}

// 5-year stat bands read as context, not competing series: a mid-grey dashed
// average with light-grey min/max (distinguished from each other by dash
// pattern, not color, since min sits below the years and max above). Grey also
// sidesteps any collision with a year's color — the old '#ff7300' max happened
// to match the year palette's orange.
export const STAT_AVG_COLOR = '#6b7280';
export const STAT_BAND_COLOR = '#b0b6c0';

/**
 * Relative luminance (WCAG) — used so a light chip background (e.g. the
 * muted-tail years or the grey stat bands) gets dark text instead of the white
 * that works for the vivid red/green/blue chips.
 */
export function isLightColor (hex: string): boolean {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
}
